import React, { useState, useEffect } from 'react';
import { itemProfileAPI } from '../../services/api';
import './SellerProfiles.css';

const GST_RATES = [0, 5, 12, 18, 28];

const EMPTY_FORM = {
  profile_name: '',
  description:  '',
  hsn_sac_code: '',
  unit:         'Nos',
  unit_price:   '',
  gst_rate:     18,
  tax_type:     'exclusive',
};

const ItemProfiles = () => {
  const [profiles, setProfiles]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editingProfile, setEditing]    = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);

  useEffect(() => { fetchProfiles(); }, []);

  const fetchProfiles = async () => {
    try {
      const res = await itemProfileAPI.getAll();
      if (res.success) setProfiles(res.data);
    } catch {
      alert('Failed to fetch item profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProfile) {
        const res = await itemProfileAPI.update(editingProfile.id, formData);
        if (res.success) { alert('Item profile updated'); fetchProfiles(); resetForm(); }
      } else {
        const res = await itemProfileAPI.create(formData);
        if (res.success) { alert('Item profile created'); fetchProfiles(); resetForm(); }
      }
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors)[0][0]
        : 'Failed to save item profile';
      alert(msg);
    }
  };

  const handleEdit = (profile) => {
    setEditing(profile);
    setFormData({
      profile_name: profile.profile_name,
      description:  profile.description,
      hsn_sac_code: profile.hsn_sac_code || '',
      unit:         profile.unit,
      unit_price:   profile.unit_price,
      gst_rate:     parseInt(profile.gst_rate, 10),
      tax_type:     profile.tax_type || 'exclusive',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item profile?')) return;
    try {
      const res = await itemProfileAPI.delete(id);
      if (res.success) { alert('Item profile deleted'); fetchProfiles(); }
    } catch {
      alert('Failed to delete item profile');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditing(null);
    setShowForm(false);
  };

  if (loading) return <div className="loading">Loading item profiles...</div>;

  return (
    <div className="seller-profiles-container">
      <div className="profiles-header">
        <h2>Item Profiles</h2>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="create-btn">
          {showForm ? 'Cancel' : '+ Add Item Profile'}
        </button>
      </div>

      {showForm && (
        <div className="profile-form-card">
          <h3>{editingProfile ? 'Edit' : 'Create'} Item Profile</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>Profile Name *</label>
                <input
                  type="text"
                  name="profile_name"
                  value={formData.profile_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Web Development, Monthly Hosting"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label>Description *</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Item description as it appears on invoice"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>HSN/SAC Code</label>
                <input
                  type="text"
                  name="hsn_sac_code"
                  value={formData.hsn_sac_code}
                  onChange={handleInputChange}
                  placeholder="e.g., 998314"
                />
              </div>
              <div className="form-field">
                <label>Unit *</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  placeholder="Nos, Hrs, Month…"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Unit Price (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="unit_price"
                  value={formData.unit_price}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>GST Rate (%) *</label>
                <select name="gst_rate" value={formData.gst_rate} onChange={handleInputChange} required>
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>{r}%</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Tax Type *</label>
                <select name="tax_type" value={formData.tax_type} onChange={handleInputChange} required>
                  <option value="exclusive">Exclusive (add tax on top)</option>
                  <option value="inclusive">Inclusive (tax included in price)</option>
                </select>
              </div>
            </div>

            {/* Live tax preview */}
            {formData.unit_price > 0 && (
              (() => {
                const price    = parseFloat(formData.unit_price) || 0;
                const rate     = parseFloat(formData.gst_rate)   || 0;
                const taxAmt   = formData.tax_type === 'inclusive'
                  ? price * rate / (100 + rate)
                  : price * rate / 100;
                const taxable  = formData.tax_type === 'inclusive'
                  ? price - taxAmt
                  : price;
                const total    = formData.tax_type === 'inclusive'
                  ? price
                  : price + taxAmt;
                return (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <span>Taxable: <strong>₹{taxable.toFixed(2)}</strong></span>
                    <span>Tax ({rate}%): <strong>₹{taxAmt.toFixed(2)}</strong></span>
                    <span>Total: <strong>₹{total.toFixed(2)}</strong></span>
                  </div>
                );
              })()
            )}

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-btn">Cancel</button>
              <button type="submit" className="submit-btn">
                {editingProfile ? 'Update' : 'Create'} Profile
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="profiles-grid">
        {profiles.length === 0 ? (
          <div className="empty-state">
            <p>No item profiles yet. Create one to reuse items across invoices.</p>
          </div>
        ) : (
          profiles.map((profile) => (
            <div key={profile.id} className="profile-card">
              <h3>{profile.profile_name}</h3>
              <div className="profile-details">
                <p><strong>{profile.description}</strong></p>
                {profile.hsn_sac_code && <p>HSN/SAC: {profile.hsn_sac_code}</p>}
                <p>Unit: {profile.unit} &nbsp;|&nbsp; Price: ₹{parseFloat(profile.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                <p>GST: {profile.gst_rate}% ({profile.tax_type === 'inclusive' ? 'Inclusive' : 'Exclusive'})</p>
              </div>
              <div className="profile-actions">
                <button onClick={() => handleEdit(profile)} className="edit-btn">Edit</button>
                <button onClick={() => handleDelete(profile.id)} className="delete-btn">Delete</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ItemProfiles;
